import { app, installations } from "@/util/firebase";
import { Fulfiller } from "@busy-human/gearbox/v2";
import { getId, onIdChange } from "firebase/installations";
import { defineStore } from "pinia";
import { computed, reactive, ref, shallowReactive, watch } from "vue";
import { deleteToken, getToken, onMessage, isSupported, getMessaging, type Messaging, Unsubscribe, MessagePayload } from "firebase/messaging";
import { NotificationTokens, Users } from "@busy-human/amt-library";
import { useCompany } from "@/store/useCompany";
import { useAuth } from "@/store/useAuth";
import { topicSubscription } from "./functions";
import { events } from "@/events";
import { clearOldNotifications, recordMessage } from "./msgdb";
import { useUsers } from "@/store/useUsers";

export enum NotificationHandled {
	Ignored = "IGNORED"
}

export type TopicHandler<T extends Record<string, string>> = (data: T, event: MessagePayload) => NotificationHandled | void;
export type TopicClickHandler<T extends Record<string, string>> = (data: T, event: MessagePayload) => void

// eslint-disable-next-line max-lines-per-function
export const useMessaging = defineStore("messaging", () => {

	const companyStore = useCompany();
	const authStore = useAuth();

	function log(...values: any[]) {
		console.log("[Messaging]", ...values);
	}

	const data = shallowReactive({
		uid: null as null | string,
		installId: null as null | string,
		userType: null as null | Users.UserType,
		serviceWorker: null as null | ServiceWorkerRegistration,
		isSupported: false,
		messaging: null as null | Messaging,
		companyId: null as null | string,
		enabled: null as null | boolean,
		token: null as null | string
	});

	const fulfiller = reactive(new Fulfiller([
		"enabled", "uid", "installId", "userType", "serviceWorker", "isSupported", "messaging", "companyId"
	], data));
	const tokenId = ref<null | string>(null);
	const unsubscribe = ref<Unsubscribe | null>(null);

	const subscribedTopics = reactive(new Set<string>());
	const ready = ref(false);

	const topicHandler = reactive<Record<string, TopicHandler<any>>>({});
	const topicClickHandler = reactive<Record<string, TopicClickHandler<any>>>({});
	const topicAutoClears = reactive<Record<string, boolean>>({});

	watch(() => companyStore.company?.$id, (id, pid) => {
		if (pid !== id && id) {
			log("Got company id", id);
			data.companyId = id;
			fulfiller.check();
		}
	}, { immediate: true });

	watch(() => authStore.currentUID, (uid, puid) => {
		if (uid && puid !== uid) {
			log("Got user id", uid);
			data.uid = uid;
			fulfiller.check();
		}
	}, { immediate: true });

	watch(() => authStore.claims?.userType, (userType, put) => {
		if (typeof userType === 'string' && put !== userType) {
			log("Got user type", userType);
			data.userType = userType as Users.UserType;
			fulfiller.check();
		}
	}, { immediate: true })

	async function handleLogout() {
		if (!data.messaging) return;
		await deleteToken(data.messaging);
		if (tokenId.value) {
			const id = tokenId.value;
			tokenId.value = null;
			await NotificationTokens.Collection.deleteDoc(id);
		}
		data.token = null;
	}

	const enabled = computed({
		get() {
			return data.enabled
		},
		set(value: boolean) {
			data.enabled = value;
			log("System enabled");
			fulfiller.check();

			if (!data.enabled) {
				handleLogout();
			}
		}
	});

	authStore.hooks.on("pre:logout", handleLogout);

	onIdChange(installations, id => {
		if (id) {
			log("Received installation id");
			data.installId = id;
			fulfiller.check();
		}
	});

	getId(installations).then(id => {
		data.installId = id;
		log("Got installation id");
		fulfiller.check();
	});

	navigator.serviceWorker?.register(new URL("../util/firebase-messaging-sw.ts", import.meta.url)).then(reg => {
		data.serviceWorker = reg;
		log("Worker registered");
		fulfiller.check();
	});

	// Handles redirects being sent from the serviceworker
	navigator.serviceWorker.addEventListener("message", (event) => {
		if (event.data.msg === 'GOTOPAGE') {
			let url = event.data.url;
			if (typeof url !== 'string') return;
			if (url.startsWith("/#")) url = url.substring(2);
			if (url.startsWith("#")) url = url.substring(1);
			events.emit('PUSHROUTE', url)
		}
	})

	isSupported().then(supported => {
		const currentUrl = new URL(window.location.href);
		if (currentUrl.host.startsWith("localhost:") || currentUrl.protocol.startsWith("https")) {
			data.isSupported = supported;
			log("Got firebase messaging");
			data.messaging = getMessaging(app);
		} else {
			log("Firebase messaging not enabled for this url");
		}
		fulfiller.check();
	});

	fulfiller.onFulfillment(async () => {
		log("Fulfiller running");
		await clearOldNotifications().catch();
		if (!data.messaging) return;
		if (!data.installId) return;

		log("Fulfiller ready, checking permission");
		let permission = Notification.permission;
		if (permission === 'denied') {
			log("Permission denied, should reenable old system");
			return;
		} else if (permission !== 'granted') {
			log("Permission not yet granted, requesting permission");
			permission = await Notification.requestPermission();
			if (permission !== 'granted') {
				log("Permission denied after request, should enable old system");
				return;
			}
		}

		const token = await getToken(data.messaging, {
			vapidKey: CONFIG.firebase.fcmPublicKey,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			serviceWorkerRegistration: data.serviceWorker!
		});

		log("GOT TOKEN", token);

		data.token = token;

		const tmp: NotificationTokens.NotificationToken = {
			...NotificationTokens.Defaults(),
			deviceId: data.installId,
			userId: data.uid ?? "",
			token,
			userType: data.userType ?? Users.UserType.CommunicationCenterUser,
			ua: navigator ? navigator.userAgent : null,
			companyId: data.companyId ?? ""
		};

		await NotificationTokens.Collection.coerceDocForDevice(data.installId, tmp);

		log("Token pushed to DB");

		ready.value = true;

		unsubscribe.value = onMessage(data.messaging, event => {
			log("Received message", event);

			// if (typeof window === 'undefined' || !('document' in window) || !window.document.hasFocus()) {
			// 	log("Tab not focused, ignoring");
			// 	return;
			// }

			const topic = event.data?.['topic'];
			if (!topic || typeof topic !== 'string') {
				log(`Received notification without topic`);
				return;
			}
			if (topicHandler[topic]?.(event.data, event) === NotificationHandled.Ignored) {
				log("Notification ignored by handler");
				return;
			}

			recordMessage(event, !topicAutoClears[topic]);
			navigator.locks.request("notification-received-sound", { ifAvailable: true }, (lock) => {
				if (lock) {
					useUsers().playNotificationSound();
				} else {
					log("Didn't receive lock, avoiding playing a sound");
				}
			})

			if ("Notification" in window && Notification.permission === "granted") {
				const notif = new Notification(event.notification?.title ?? "", {
					body: event.notification?.body,
					tag: event.messageId
				});
				notif.addEventListener("click", () => {
					topicClickHandler[topic]?.(event.data, event);
				});
				log("Created notification", notif);
			} else {
				log("Can't create notification");
			}
		});

		for (const topic of subscribedTopics) {
			try {
				await topicSubscription({
					token, topic, action: 'sub'
				});
				log(`Subscribed to topic ${topic}`);
			} catch (e) {
				console.error(e);
			}
		}
	});

	function emitNotificationClick(notification: MessagePayload) {
		const topic = notification.data?.topic;
		if (typeof topic !== 'string') {
			console.warn("Emitted notification click without a topic", notification);
			return;
		}

		topicClickHandler[topic]?.(notification.data, notification);
	}

	async function subscribeToTopic(topic: string) {
		if (subscribedTopics.has(topic)) return;

		subscribedTopics.add(topic);

		log(`Added topic ${topic} to subscribed topics`);

		if (!data.token) return;

		if (ready.value) {
			await topicSubscription({
				token: data.token,
				topic,
				action: "sub"
			});

			log(`Subscribed to topic ${topic}`);
		}
	}

	async function unsubscribeFromTopic(topic: string) {
		if (!subscribedTopics.has(topic)) return;

		subscribedTopics.delete(topic);

		log(`Removed topic ${topic} from subscribed topics`)

		if (!data.token) return;

		if (ready.value) {
			await topicSubscription({
				token: data.token,
				topic,
				action: "unsub"
			});

			log(`Unsubscribed from topic ${topic}`);
		}
	}

	async function addTopicHandler<T extends Record<string, string>>(topic: string, opts: { disableAutoclear?: boolean, onReceived?: TopicHandler<T>, onClicked?: TopicClickHandler<T> } = {}) {
		await subscribeToTopic(topic);
		if (opts.onReceived) topicHandler[topic] = opts.onReceived;
		if (opts.onClicked) topicClickHandler[topic] = opts.onClicked;
		topicAutoClears[topic] = !opts.disableAutoclear;
	}

	async function removeTopicHandler(topic: string) {
		await unsubscribeFromTopic(topic);
		delete topicHandler[topic];
		delete topicClickHandler[topic];
		delete topicAutoClears[topic];
	}

	return { handleLogout, subscribedTopics, enabled, subscribeToTopic, unsubscribeFromTopic, addTopicHandler, removeTopicHandler, emitNotificationClick };
});